import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface PopoverProps {
  /** Render prop for the trigger; spread `props` onto a <button>. */
  trigger: (props: { 'aria-expanded': boolean; 'aria-controls': string; 'aria-haspopup': 'dialog' | 'menu'; onClick: () => void }) => ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'left' | 'right';
  /** Open above the trigger (footers / bottom action bars). */
  placement?: 'bottom' | 'top';
  className?: string;
  role?: 'dialog' | 'menu';
  label?: string;
  onOpenChange?: (open: boolean) => void;
}

/** Click-to-open floating panel (menus, evidence popovers). Closes on outside click and Escape, returns focus. */
export function Popover({ trigger, children, align = 'right', placement = 'bottom', className, role = 'dialog', label, onOpenChange }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);

  const set = (v: boolean) => {
    setOpen(v);
    onOpenChange?.(v);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) set(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        set(false);
        wrap.current?.querySelector<HTMLElement>('[aria-controls]')?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => set(false);

  return (
    <div ref={wrap} className="relative inline-flex">
      {trigger({ 'aria-expanded': open, 'aria-controls': id, 'aria-haspopup': role, onClick: () => set(!open) })}
      {open && (
        <div
          id={id}
          role={role}
          aria-label={label}
          className={cn(
            placement === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            'absolute z-40 min-w-[220px] rounded-lg border border-line bg-surface shadow-[0_12px_32px_-16px_rgba(0,0,0,0.3)]',
            align === 'right' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ children, onClick, icon, danger }: { children: ReactNode; onClick: () => void; icon?: ReactNode; danger?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn('flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-surface-2', danger ? 'text-danger' : 'text-ink')}
    >
      {icon}
      {children}
    </button>
  );
}
